import React, { useState, useEffect } from 'react';
import './Review.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import reviewService from '../api/reviewService'; 
import { Link } from 'react-router-dom';

const Review = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    rating: 0, // Defaults to 0 (unselected)
    feedback: "" 
  });
  
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(0); 

  useEffect(() => {
    // Force window to scroll to the very top instantly when the component mounts
    window.scrollTo(0, 0);
    AOS.init({ duration: 1000, once: true });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  
  // 1. Validation: Check Name Field
  if (!formData.name || formData.name.trim().length < 2) {
    setStatus({ 
      type: "danger", 
      msg: "Please enter your full name (at least 2 characters long)." 
    });
    return;
  }

  // 2. Validation: Check Email Format
  // const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // if (!formData.email || !emailRegex.test(formData.email)) {
  //   setStatus({ 
  //     type: "danger", 
  //     msg: "Please enter a valid email address." 
  //   });
  //   return;
  // }
  
  // 3. Validation: Check Star Rating Selection
  if (formData.rating === 0) {
    setStatus({ 
      type: "danger", 
      msg: "Please select a rating between 1 and 5 stars." 
    });
    document.getElementById("star-rating-section")?.scrollIntoView({ behavior: 'smooth' });
    return;
  }

  // 4. Validation: Check Feedback Message Length
  if (!formData.feedback || formData.feedback.trim().length < 10) {
    setStatus({ 
      type: "danger", 
      msg: "Please write a bit more feedback (at least 10 characters long)." 
    });
    return;
  }

  // If all client-side validations pass, proceed to API call
  setLoading(true);
  setStatus({ type: "info", msg: "Submitting your review..." });

  try {
    const response = await reviewService.createReview(formData);

    if (response && response.success) {
      setStatus({ type: "success", msg: "Thank you for your feedback!" });
      setFormData({ name: "", email: "", rating: 0, feedback: "" }); // Reset form
    } else {
      setStatus({ type: "danger", msg: response.message || "Failed to submit review." });
    }
  } catch (error) {
    const serverError = error.response?.data?.error || error.response?.data?.message;
    setStatus({ 
      type: "danger", 
      msg: serverError || "Submission failed. Please try again." 
    });
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="services-page">
      <section className="services-hero-container">
        <div className="hero-overlay">
          <div className="container text-center">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb justify-content-center">
                <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                <li className="breadcrumb-item active text-white" aria-current="page">Write a Review</li>
              </ol>
            </nav>
            <h1 className="display-3 fw-bold text-white">Client Feedback</h1>
          </div>
        </div>
      </section>
      
      <section className="contact-section py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="contact-form-card p-5 bg-white shadow-sm" style={{ borderRadius: '20px' }}>
                <h6 className="text-uppercase text-primary fw-bold small">Share Your Experience</h6>
                <h2 className="display-6 fw-bold mb-4">Leave a Review</h2>
                
                {status.msg && (
                  <div className={`alert alert-${status.type} alert-dismissible fade show`} role="alert">
                    {status.msg}
                    <button type="button" className="btn-close" onClick={() => setStatus({type:"", msg:""})}></button>
                  </div>
                )}

                <form className="row g-4" onSubmit={handleSubmit}>
                  {/* Name & Email */}
                  <div className="col-md-6">
                    <label className="small fw-bold text-muted mb-2">FULL NAME</label>
                    <input 
                      type="text" name="name" className="form-control" 
                      placeholder="e.g. John Doe" value={formData.name}
                      onChange={handleChange}  
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="small fw-bold text-muted mb-2">EMAIL ADDRESS</label>
                    <input 
                      type="email" name="email" className="form-control" 
                      placeholder="john@example.com" value={formData.email}
                      onChange={handleChange}  
                    />
                  </div>

                  {/* Star Rating System Validation Wrapper */}
                  <div id="star-rating-section" className="col-12 text-center py-3">
                    <label className="d-block small fw-bold text-muted mb-3">YOUR RATING</label>
                    <div className="star-rating">
                      {[...Array(5)].map((star, index) => {
                        const ratingValue = index + 1;
                        const isFilled = ratingValue <= (hover || formData.rating);
                        
                        return (
                          <button
                            type="button"
                            key={ratingValue}
                            className={`btn p-1 border-0 bg-transparent fs-2 ${isFilled ? "text-warning" : "text-secondary"}`}
                            style={{ transition: '0.2s', opacity: isFilled ? 1 : 0.4 }}
                            onClick={() => setFormData({ ...formData, rating: ratingValue })}
                            onMouseEnter={() => setHover(ratingValue)}
                            onMouseLeave={() => setHover(0)}
                          >
                            <i className={`bi ${isFilled ? 'bi-star-fill' : 'bi-star'}`}></i>
                          </button>
                        );
                      })}
                    </div>
                    
                    {/* Informative message depending on validation status */}
                    <p className={`small mt-2 fw-semibold ${formData.rating > 0 ? "text-muted" : "text-danger"}`}>
                      {formData.rating > 0 
                        ? `Selected: ${formData.rating} out of 5 Stars` 
                        : "* Please select a star rating."
                      }
                    </p>
                  </div>

                  {/* Feedback Message */}
                  <div className="col-12">
                    <label className="small fw-bold text-muted mb-2">YOUR FEEDBACK</label>
                    <textarea 
                      name="feedback" className="form-control" rows="5" 
                      placeholder="Tell us about your experience with our services..."
                      value={formData.feedback} onChange={handleChange} 
                    ></textarea>
                  </div>

                  <div className="col-12 text-center">
                    <button type="submit" className="btn btn-dark px-5 py-3 fw-bold shadow-sm" disabled={loading}>
                      {loading ? (
                        <span className="spinner-border spinner-border-sm me-2"></span>
                      ) : (
                        <>Submit Review <i className="bi bi-send-fill ms-2"></i></>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Review;